import * as React from "react";

export type Platform = "ios" | "android" | "desktop";

export interface DeviceInfo {
  platform: Platform;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
}

/**
 * Detects the user's OS platform via `navigator.userAgent`. Result is stable
 * for the session and shared across all callers. Use for platform-specific
 * copy (e.g. "Tap the lock icon" vs. "Open iOS Settings").
 *
 * For viewport-based layout decisions, use `useIsMobile` from
 * `./use-mobile` instead.
 */
export function usePlatform(): DeviceInfo {
  return React.useSyncExternalStore(
    subscribePlatform,
    getPlatformSnapshot,
    getPlatformServerSnapshot,
  );
}

// Platform is immutable within a session; no subscription needed.
const subscribePlatform = () => () => {};

const SERVER_PLATFORM: DeviceInfo = {
  platform: "desktop",
  isIOS: false,
  isAndroid: false,
  isDesktop: true,
};

function getPlatformServerSnapshot(): DeviceInfo {
  return SERVER_PLATFORM;
}

// Cached so every call returns the same object reference, preventing spurious
// re-renders when consumers destructure the result.
let cachedPlatform: DeviceInfo | null = null;

function getPlatformSnapshot(): DeviceInfo {
  if (cachedPlatform) {
    return cachedPlatform;
  }
  cachedPlatform = detectPlatform({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
  });
  return cachedPlatform;
}

interface DetectPlatformInput {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}

/**
 * Pure function form of platform detection. Exported for unit tests. * production code should use the `usePlatform` hook.
 *
 * `navigator.platform` is officially deprecated but still ships in every
 * browser (and will for years due to web-compat). It's used here because
 * there is no viable replacement for this specific detection:
 * `navigator.userAgentData` lacks Safari/Firefox support and cannot
 * identify iPadOS 13+, which masquerades as desktop Mac Safari.
 * `maxTouchPoints > 1` is the standard disambiguation.
 */
export function detectPlatform({
  userAgent,
  platform,
  maxTouchPoints,
}: DetectPlatformInput): DeviceInfo {
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1);
  const isAndroid = /Android/i.test(userAgent);
  const detected: Platform = isIOS ? "ios" : isAndroid ? "android" : "desktop";
  return {
    platform: detected,
    isIOS,
    isAndroid,
    isDesktop: detected === "desktop",
  };
}
